import { Injectable } from '@angular/core';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import {
  AuthCredential,
  AuthError,
  getAuth,
  GoogleAuthProvider,
  OAuthCredential,
  OAuthProvider,
  signInWithCredential,
} from '@angular/fire/auth';
import { FirebaseService } from '@smartflip/data-firebase';

@Injectable({
  providedIn: 'root',
})
export class NativeLoginService {
  constructor(private firebaseService: FirebaseService) {}

  async nativeGoogleLogin(): Promise<OAuthCredential> {
    // 1. Create credentials on the native layer
    const result = await FirebaseAuthentication.signInWithGoogle();
    // 2. Sign in on the web layer using the id token
    const auth = getAuth();
    const credential = GoogleAuthProvider.credential(
      result.credential?.idToken,
    );
    await signInWithCredential(auth, credential);

    return credential;
  }

  async nativeFacebookLogin(): Promise<OAuthCredential> {
    // For Facebook Limited Login, we need to use a different approach
    // Limited Login tokens are not compatible with FacebookAuthProvider.credential()
    const result = await FirebaseAuthentication.signInWithFacebook({
      useLimitedLogin: true,
      skipNativeAuth: true,
    });

    if (!result.credential?.idToken) {
      throw new Error('No ID token received from Facebook Limited Login');
    }

    // For Limited Login, use OAuthProvider instead of FacebookAuthProvider
    const provider = new OAuthProvider('facebook.com');
    const credential = provider.credential({
      idToken: result.credential.idToken,
      rawNonce: result.credential.nonce, // Include the nonce for security
    });

    const auth = getAuth();
    
    await signInWithCredential(auth, credential);

    return credential;
  }

  async nativeAppleLogin(): Promise<OAuthCredential> {
    const result = await FirebaseAuthentication.signInWithApple({
      skipNativeAuth: true,
    });
    const provider = new OAuthProvider('apple.com');
    const credential = provider.credential({
      idToken: result.credential?.idToken,
      rawNonce: result.credential?.nonce,
    });
    const auth = getAuth();

    await signInWithCredential(auth, credential);

    return credential;
  }

  // TODO: refactor code to use this again and simplify the native logins
  async completeLogin(authCredential: AuthCredential) {
    // Make Firebase aware of the login
    return signInWithCredential(this.firebaseService.auth, authCredential)
      .then(() => {
        return this.firebaseService.auth.currentUser;
      })
      .catch((e: AuthError) => {
        //TODO: Check for same creds error
        alert('Error with SignInCredentials: \n' + JSON.stringify(e));
        return null;
      });
  }
}
